import ArticleLayout from '@/components/feature/ArticleLayout';
import { useLang } from '@/hooks/useLang';

export default function AllChinaBuyWarehousePage() {
  const currentLang = useLang();

  return (
    <ArticleLayout
      title="AllChinaBuy Warehouse Guide — Storage, QC & Shipping Prep"
      metaDesc="Complete guide to AllChinaBuy warehouse services including storage, QC inspections, parcel consolidation, and shipping preparation. Understand how warehouse processes work."
      h1="AllChinaBuy Warehouse Guide — Storage, QC & Shipping Prep"
      author="Editorial Team"
      publishedDate="2026-05-01"
      updatedDate="2026-08-26"
      breadcrumbs={[
        { label: 'Home', href: `/${currentLang}/` },
        { label: 'Warehouse', href: `/${currentLang}/allchinabuy-warehouse/` },
      ]}
    >
      <div className="prose max-w-none">
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
          <p className="text-lg text-foreground-800 leading-relaxed m-0">
            The <strong>AllChinaBuy warehouse</strong> is where purchased items are stored, inspected, and prepared for international shipping. Understanding warehouse processes — including storage duration, QC inspections, parcel consolidation, and packaging options — helps you make better purchasing decisions and manage your orders efficiently.
          </p>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">How the Warehouse Process Works</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          When you purchase an item through a buying-agent platform, the product is first shipped to the platform\'s warehouse. Upon arrival, the item is logged, photographed, and made available for your review. This warehouse stage is critical because it provides the opportunity to inspect products before committing to expensive international shipping.
        </p>
        <ol className="list-decimal list-inside space-y-3 text-foreground-700 mb-6 pl-1">
          <li><strong>Item arrival</strong> — The purchased item arrives at the warehouse from the seller.</li>
          <li><strong>Logging & weighing</strong> — The item is logged into the system and weighed.</li>
          <li><strong>QC photography</strong> — Photos are taken to document the item\'s condition.</li>
          <li><strong>Storage</strong> — The item is stored until you approve shipping or request a return.</li>
          <li><strong>Shipping preparation</strong> — Once approved, the item is packaged and prepared for dispatch.</li>
          <li><strong>Dispatch</strong> — The parcel is handed to the selected carrier for delivery.</li>
        </ol>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Storage Duration & Policies</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Most buying-agent platforms offer free storage for a limited period, typically ranging from 30 to 90 days. After the free storage period expires, daily storage fees may apply. Items left in storage for extended periods without action may be subject to disposal policies.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          It is recommended to plan your purchases so that items arrive within a similar timeframe, allowing you to consolidate and ship them together before free storage expires.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Parcel Consolidation</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Parcel consolidation is one of the most valuable warehouse services. Instead of shipping multiple small parcels individually, you can combine them into a single shipment. This approach typically reduces per-parcel handling fees and can lower overall shipping costs.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Consolidation also allows you to optimize packaging. You can choose to remove unnecessary individual packaging (such as shoeboxes) to reduce weight and volume, further lowering shipping costs. However, be aware that consolidated parcels may have different insurance and customs implications compared to individual shipments.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Packaging Options</h2>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Option</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Description</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">When to Use</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[
                ['Original Packaging', 'Keep all original boxes and wrapping', 'Gift items, collectibles, resale'],
                ['Remove Shoeboxes', 'Discard shoeboxes to reduce weight', 'Personal use, shipping cost reduction'],
                ['Repacking', 'Repack items into smaller, lighter packaging', 'Multiple items, consolidation'],
                ['Bubble Wrap', 'Extra protection with bubble wrap', 'Fragile items, electronics'],
                ['Moisture Protection', 'Add moisture-absorbing materials', 'Humid destinations, long shipping'],
              ].map(([option, desc, when]) => (
                <tr key={option} className="bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-800">{option}</td>
                  <td className="px-4 py-3 text-foreground-600">{desc}</td>
                  <td className="px-4 py-3 text-foreground-600">{when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Returns & Exchanges at the Warehouse</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          If a QC inspection reveals issues with the product, you may have the option to request a return or exchange before the item is shipped internationally. This is a significant advantage of using a buying-agent platform, as returns to local sellers within China are much easier and cheaper than international returns.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          Return policies and time limits vary by platform. Typically, you must request a return within a specific window (often 7-14 days after arrival at the warehouse). Some sellers may refuse returns, so understanding the platform\'s policies and the seller\'s terms is essential before purchasing.
        </p>

        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
            Explore Products on AllChinaBuy
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            Browse the spreadsheet to find products and plan your warehouse strategy.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors"
          >
            Visit Main Website →
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-background-200/70">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong>Disclaimer:</strong> This guide provides general information about warehouse processes. Specific policies, storage durations, fees, and services vary by platform and may change over time. Always verify current warehouse policies directly with the platform before purchasing. We are not affiliated with any buying-agent platform and cannot guarantee service availability or quality.
          </p>
        </div>
      </div>
    </ArticleLayout>
  );
}